import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// ---- Per-assessment capability tokens (SEC-001) -------------------------
// The backend mints an `owner_token` when an assessment is created. We store it
// (keyed by assessment id) and attach it as X-Owner-Token on every per-assessment
// request so only the creator's browser (or the logged-in owner) can read/write it.
const OWNER_KEY = "s2s_owner_tokens_v1";
function _readOwnerMap() {
  try { return JSON.parse(localStorage.getItem(OWNER_KEY) || "{}"); }
  catch { return {}; }
}
export function saveOwnerToken(id, token) {
  if (!id || !token) return;
  try {
    const m = _readOwnerMap();
    m[id] = token;
    localStorage.setItem(OWNER_KEY, JSON.stringify(m));
  } catch (e) { console.warn("saveOwnerToken failed", e); }
}
export function getOwnerToken(id) {
  if (!id) return null;
  return _readOwnerMap()[id] || null;
}

// Attach the capability token to any /assessment/{id}... request.
api.interceptors.request.use((config) => {
  const m = (config.url || "").match(/\/assessment\/([^/?#]+)/);
  if (m) {
    const token = getOwnerToken(m[1]);
    if (token) config.headers["X-Owner-Token"] = token;
  }
  return config;
});

// Normalise FastAPI/Pydantic 422 errors. Their `detail` is an array of objects
// ({type, loc, msg, input, url}); passing that straight to a toast or JSX crashes
// React ("Objects are not valid as a React child"). Coerce it to a string here so
// every `e.response.data.detail` consumer across the app stays safe.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const d = error?.response?.data?.detail;
    if (Array.isArray(d)) {
      error.response.data.detail =
        d.map((x) => (typeof x === "string" ? x : x?.msg)).filter(Boolean).join("; ") ||
        "Validation error";
    } else if (d && typeof d === "object") {
      error.response.data.detail = d.msg || JSON.stringify(d);
    }
    return Promise.reject(error);
  }
);

export async function getQuestions(mode) {
  const { data } = await api.get(`/questions/${mode}`);
  return data;
}

export async function createAssessment(mode, organization, email, extras = {}) {
  const { data } = await api.post(`/assessment`, {
    mode,
    organization,
    email,
    industry: extras.industry || undefined,
    department: extras.department || undefined,
    org_size: extras.org_size || undefined,
    country: extras.country || undefined,
    is_advisor: extras.is_advisor || undefined,
    advisor_firm: extras.advisor_firm || undefined,
    client_name: extras.client_name || undefined,
  });
  if (data?.id && data?.owner_token) saveOwnerToken(data.id, data.owner_token);
  return data;
}

export async function getAssessment(id) {
  const { data } = await api.get(`/assessment/${id}`);
  return data;
}

export async function submitAnswer(id, question_id, answer) {
  const { data } = await api.post(`/assessment/${id}/answer`, { question_id, answer });
  return data;
}

export async function generateReport(id, organization) {
  const { data } = await api.post(`/assessment/${id}/generate-report`, { organization });
  return data;
}

export async function getReport(id) {
  const { data } = await api.get(`/assessment/${id}/report`);
  return data;
}

export function pdfUrl(id) { return `${API}/assessment/${id}/pdf`; }

async function _downloadBlob(path, filename) {
  const resp = await api.get(path, { responseType: "blob" });
  const blobUrl = window.URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
}

export async function downloadDocx(id) {
  await _downloadBlob(`/assessment/${id}/docx`, `business-resilience-assessment-${id.slice(0, 8)}.docx`);
}

export async function downloadXlsx(id) {
  await _downloadBlob(`/assessment/${id}/xlsx`, `resilience-tasks-and-risks-${id.slice(0, 8)}.xlsx`);
}

export async function getAccess(id) {
  const { data } = await api.get(`/payments/access/${id}`);
  return data;
}

export async function listTasks(id) {
  const { data } = await api.get(`/assessment/${id}/tasks`);
  return data.tasks || [];
}
export async function addTask(id, task) {
  const { data } = await api.post(`/assessment/${id}/tasks`, task);
  return data.tasks || [];
}
export async function updateTask(id, taskId, task) {
  const { data } = await api.put(`/assessment/${id}/tasks/${taskId}`, task);
  return data.tasks || [];
}
export async function deleteTask(id, taskId) {
  const { data } = await api.delete(`/assessment/${id}/tasks/${taskId}`);
  return data.tasks || [];
}
export async function regenerateManaged(id) {
  const { data } = await api.post(`/assessment/${id}/regenerate-managed`);
  return data;
}

// Mint/reuse the owner's feedback token so any entry point can deep-link to the
// testimonial form. Requires the assessment owner token (sent by the interceptor).
export async function getFeedbackToken(id) {
  const { data } = await api.post(`/assessment/${id}/feedback-token`);
  return data.feedback_token;
}

// Download the PDF via axios so the X-Owner-Token header is sent (a plain <a href>
// browser GET cannot carry the capability token). Triggers a file download.
export async function downloadPdf(id) {
  const resp = await api.get(`/assessment/${id}/pdf`, { responseType: "blob" });
  const blobUrl = window.URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = `business-resilience-assessment-${id.slice(0, 8)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
}

// Resume helpers — store ONLY a pointer (assessment id + mode), never answers/PII.
// localStorage is acceptable here: the data is non-sensitive UX state and we hold no auth tokens
// (auth lives in an httpOnly cookie set by /api/auth/session).
const RESUME_KEY = "s2s_resume_v1";
export function saveResume(payload) {
  try {
    localStorage.setItem(RESUME_KEY, JSON.stringify({ ...payload, ts: Date.now() }));
  } catch (e) {
    console.warn("saveResume failed", e);
  }
}
export function readResume() {
  try {
    return JSON.parse(localStorage.getItem(RESUME_KEY) || "null");
  } catch (e) {
    console.warn("readResume parse failed", e);
    return null;
  }
}
export function clearResume() {
  try {
    localStorage.removeItem(RESUME_KEY);
  } catch (e) {
    console.warn("clearResume failed", e);
  }
}
